import { MatchStats, Player, PlayerHistory } from '../types';

/**
 * Compact encoding of a game's throws, for the live feed and the saved result.
 *
 * WHY THIS EXISTS. Tournament results used to keep only aggregates (accuracy,
 * triples, freezes). The throws themselves were thrown away the moment a match
 * was saved, and the live feed carried only the LAST throw. That makes two
 * things impossible: telling a spectator how a game is going (live win
 * probability, a momentum graph) and calibrating any model of it afterwards,
 * because the data to fit one against is not being kept.
 *
 * Token format, one per turn:
 *     "530"        dice values, one digit per die (0 = die scored nothing)
 *     "624.010"    ...with a frozen mask after "." — only written if any die was frozen
 *     "666*"       "*" suffix = this turn was a final-throw attempt
 * A player's turns are joined with ",":   "530,624.010,666*"
 *
 * The score of a turn is deliberately NOT stored. It is derivable from the dice
 * by the real rules engine (applyDiceRules), so a rules change cannot leave the
 * stored log disagreeing with the game, and the log stays about a quarter of
 * the size.
 */

export interface DecodedTurn {
  dice: number[];
  frozen: boolean[];
  finalThrow: boolean;
}

const TOKEN = /^([0-6]{1,6})(?:\.([01]{1,6}))?(\*)?$/;

/** Hard cap on one player's log string — a runaway game must not bloat a write. */
const MAX_LOG_CHARS = 2000;

export const encodeTurn = (entry: Pick<PlayerHistory, 'diceValues' | 'isFrozen' | 'isFinalThrowAttempt'>): string => {
  const dice = (entry.diceValues || []).map((v) => Math.max(0, Math.min(6, Math.trunc(v) || 0)));
  const frozen = Array.isArray(entry.isFrozen) ? entry.isFrozen : [];
  let out = dice.join('');
  if (frozen.some(Boolean)) {
    out += '.' + dice.map((_, i) => (frozen[i] ? '1' : '0')).join('');
  }
  if (entry.isFinalThrowAttempt) out += '*';
  return out;
};

export const encodeTurns = (history: PlayerHistory[] | undefined): string => {
  if (!history?.length) return '';
  const joined = history.map(encodeTurn).join(',');
  // Keep the most RECENT turns if it ever overflows: recent state matters most
  // for the live feed, and a real game never gets near the cap.
  return joined.length <= MAX_LOG_CHARS ? joined : joined.slice(joined.length - MAX_LOG_CHARS).replace(/^[^,]*,/, '');
};

export const decodeTurn = (token: string): DecodedTurn | null => {
  const m = TOKEN.exec(token.trim());
  if (!m) return null;
  const dice = m[1].split('').map(Number);
  const mask = m[2] ? m[2].split('').map((c) => c === '1') : [];
  return {
    dice,
    frozen: dice.map((_, i) => mask[i] === true),
    finalThrow: m[3] === '*',
  };
};

/** Decodes a whole log, silently dropping any token it cannot read. */
export const decodeTurns = (log: string | number | undefined | null): DecodedTurn[] => {
  if (log === undefined || log === null || log === '') return [];
  return String(log)
    .split(',')
    .map(decodeTurn)
    .filter((t): t is DecodedTurn => t !== null);
};

// ─── Saved result ─────────────────────────────────────────────────────────────

/**
 * `{ g1: { <uid>: "530,624.010", <uid>: "..." }, g2: { ... } }` — game → player → log.
 *
 * The keys are "g1", "g2", NOT "1", "2". Firebase hands an object whose keys
 * are small consecutive integers back as an ARRAY — `{1:…, 2:…}` reads as
 * `[<hole>, …, …]` — which silently changes the shape under every reader. This
 * was caught against the real database; the unit tests could not see it.
 */
export type ResultTurnLog = { [game: string]: { [uid: string]: string } };

/** Key for game number `n` — never a bare integer, see ResultTurnLog. */
export const gameKey = (n: number): string => `g${n}`;

/**
 * Builds the turn log persisted on a finished match, from the per-game
 * histories the game already keeps. Returns undefined when there is nothing to
 * store — an empty object would be written as null by RTDB anyway.
 */
export const buildResultTurnLog = (
  matchHistory: MatchStats[] | undefined,
  players: Player[]
): ResultTurnLog | undefined => {
  const out: ResultTurnLog = {};

  if (matchHistory?.length) {
    matchHistory.forEach((game, i) => {
      const key = gameKey(game.matchNumber || i + 1);
      (game.playerStats || []).forEach((ps) => {
        const log = encodeTurns(ps.history);
        if (!log) return;
        (out[key] = out[key] || {})[ps.playerId] = log;
      });
    });
  } else {
    // Single game (best of 1): the one game is still on the players.
    players.forEach((p) => {
      const log = encodeTurns(p.history);
      if (!log) return;
      (out[gameKey(1)] = out[gameKey(1)] || {})[p.id] = log;
    });
  }

  return Object.keys(out).length ? out : undefined;
};

// ─── Live feed ────────────────────────────────────────────────────────────────

/**
 * Every field the game device publishes to `liveScores/{matchId}` for the
 * broadcast screen. Kept as one pure function so the whole contract is in a
 * single place and can be tested without a running game.
 *
 *   uid           current score
 *   uid_wins      games won in the series
 *   uid_ft        1 while attempting the winning throw
 *   uid_dice      last throw's dice, "5,3,0"
 *   uid_frozen    which of them were frozen, "0,1,0"
 *   uid_turn      points that throw scored
 *   uid_seq       turns played this game — bumps once per turn
 *   uid_log       every throw this game, see the format above
 *   _cur          uid of the player whose turn it is
 *
 * `_cur` and `_log` are what a live win-probability needs and the feed never
 * carried: who is about to throw, and how each player has actually been doing.
 */
export const buildLiveFeed = (
  players: Player[],
  currentPlayerIndex: number
): { [key: string]: number | string } => {
  const feed: { [key: string]: number | string } = {};

  players.forEach((p) => {
    feed[p.id] = p.score;
    feed[p.id + '_wins'] = p.matchWins || 0;
    feed[p.id + '_ft'] = p.isFinalThrow ? 1 : 0;

    const last = p.history?.[p.history.length - 1];
    if (last) {
      feed[p.id + '_dice'] = (last.diceValues || []).join(',');
      feed[p.id + '_frozen'] = (last.isFrozen || []).map((f) => (f ? 1 : 0)).join(',');
      feed[p.id + '_turn'] = last.turnScore ?? 0;
      feed[p.id + '_seq'] = p.history.length;
      feed[p.id + '_log'] = encodeTurns(p.history);
    }
  });

  const current = players[currentPlayerIndex];
  if (current) feed._cur = current.id;

  return feed;
};

/** Reads one player's current-game throws back out of a live-score entry. */
export const parseLiveTurns = (
  scores: { [key: string]: number | string } | undefined,
  uid: string
): DecodedTurn[] => decodeTurns(scores?.[`${uid}_log`]);
